import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getBatchUserSubscriptions} from 'src/openapi/admin/apis/UserLcdSubscriptionsApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    userGroupsSearchResultLcdSubscriptionsActions,
    userGroupsSearchResultLcdSubscriptionsDataSelector,
    userGroupsSearchResultLcdSubscriptionsResultSelector,
} from '../slices/user-search-result-lcd-subscriptions.slice';
import {userSearchResultStateSelector} from '../slices/user-search-result-state.slice';

import type {UserSearchResultCacheKeys} from '../typedef';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';

type Props = {
    userIds: number[],
};

type LcdSubscriptionsData = Record<string, Record<string, Array<RegionSettings>>>;

export const useGetUserSearchResultLcdSubscriptions = ({userIds}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback(
        () => apiDispatch(getBatchUserSubscriptions({
            getLcdSubscriptionsRequest: {userIds},
        })),
        [userIds, apiDispatch],
    );

    const setLoadedData = useCallback((
        response: LcdSubscriptionsData,
        dataCacheKeys: UserSearchResultCacheKeys,
    ) => {
        dispatch(userGroupsSearchResultLcdSubscriptionsActions.onSuccess({
            cacheKeys: dataCacheKeys,
            totalCount: userIds.length,
            data: response,
        }));
    }, [userIds.length, dispatch]);

    return useCachedTable({
        dataSelector: userGroupsSearchResultLcdSubscriptionsDataSelector,
        stateSelector: userSearchResultStateSelector,
        resultSelector: userGroupsSearchResultLcdSubscriptionsResultSelector,
        actions: userGroupsSearchResultLcdSubscriptionsActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
    });
};
